import styled, { DefaultTheme, useTheme } from 'styled-components';
import { Marker, Polyline, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import L, { LeafletMouseEvent } from 'leaflet';

import { AppState } from '../../../store/interfaces';
import { setSelectedObject } from '../../../store/map/actions';
import { isRide, Ride } from '../../../types/ride';
import { useRoutes } from '../useRoutes';

const RideMarkerTooltip = styled(Tooltip)`
  border: none;
  box-shadow: unset;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  &::before {
    display: none;
  }

  span {
    position: relative;
    top: 8px;
  }
`;

const getStartIcon = (isSelected: boolean) =>
  L.icon({
    iconUrl: '/assets/map/pin_start.svg',
    iconSize: isSelected ? [36, 46] : [30, 41],
  });

const getEndIcon = (isSelected: boolean) =>
  L.icon({
    iconUrl: '/assets/map/pin_finish.svg',
    iconSize: isSelected ? [36, 46] : [30, 41],
  });

interface RidesMarkersProps {
  rides?: Ride[];
}

export const RidesMarkers: React.FC<RidesMarkersProps> = React.memo(function ({ rides }) {
  const dispatch = useDispatch();
  const theme: DefaultTheme = useTheme();
  const map = useMap();

  const { selectedObject, routes } = useSelector(
    (app: AppState) => ({
      selectedObject: app.map.selectedObject,
      routes: app.map.routes,
    }),
    shallowEqual
  );

  useRoutes(rides);

  useMapEvents({
    click() {
      dispatch(setSelectedObject(null));
    },
  });

  useEffect(() => {
    if (!isRide(selectedObject)) {
      return;
    }

    map.invalidateSize();

    /* if (!routes) {
      return;
    }

    if (!routes[selectedObject.id]) {
      return;
    } */

    const bounds: any = [
      // ...routes[selectedObject.id],
      {
        lat: selectedObject.start_lat,
        lng: selectedObject.start_lng,
      },
      {
        lat: selectedObject.end_lat,
        lng: selectedObject.end_lng,
      },
    ];

    map.fitBounds(bounds);
  }, [selectedObject]);

  if (!rides) {
    return null;
  }

  if (rides.length === 0) {
    return null;
  }

  return (
    <>
      {rides
        .map((ride, index) => {
          const eventHandlers = {
            click: (e: LeafletMouseEvent) => {
              e.originalEvent.stopPropagation();
              dispatch(setSelectedObject(ride));
            },
          };

          const isNeedOpacity =
            selectedObject && isRide(selectedObject) && selectedObject.id !== ride.id;

          const isSelected = ride === selectedObject;
          const spanStyle = isSelected
            ? { color: theme.map.routes.selected, fontSize: 18, top: 10, zIndex: 4050 }
            : {};

          return [
            routes[ride.id] ? (
              <Polyline
                // @ts-ignore
                color={isSelected ? theme.map.routes.selected : theme.map.routes.default}
                key={`route${ride.id}${isSelected}${isNeedOpacity}`}
                weight={isSelected ? 6 : 3}
                opacity={isNeedOpacity ? 0.3 : 1}
                positions={routes[ride.id]}
              />
            ) : null,
            <Marker
              icon={getStartIcon(isSelected)}
              key={`${ride.id}-start`}
              position={[ride.start_lat, ride.start_lng]}
              eventHandlers={eventHandlers}
              opacity={isNeedOpacity ? 0.7 : 1}
              zIndexOffset={isSelected ? 4000 : 1}
            >
              <RideMarkerTooltip offset={[0, 8]} direction="top" opacity={1} permanent>
                <span style={spanStyle}>{/* index + 1 */}</span>
              </RideMarkerTooltip>
            </Marker>,
            <Marker
              icon={getEndIcon(isSelected)}
              key={`${ride.id}-end`}
              position={[ride.end_lat, ride.end_lng]}
              eventHandlers={eventHandlers}
              opacity={isNeedOpacity ? 0.7 : 1}
              zIndexOffset={isSelected ? 4000 : 1}
            >
              <RideMarkerTooltip offset={[0, 8]} direction="top" opacity={1} permanent>
                <span style={spanStyle}>{/* index + 1 */}</span>
              </RideMarkerTooltip>
            </Marker>,
          ];
        })
        .flat()}
    </>
  );
});
